import React, { useState, useEffect, useRef, useCallback } from "react";
import { useWebhookEvents } from '../../../hooks/useWebhookEvents';
import { useAccess } from '../../../hooks/useAccessScreen';
import { useDataAuth } from '../../../hooks/useDataAuth';
import { useMediaQuery, useTheme } from '@mui/material';
import { useLocation, useNavigate } from 'react-router';
import Grid from '@mui/material/Grid';
import Box from "@mui/material/Box";
import { DialogSelectCompany } from '../../../components/dialogs/DialogSelectCompany';
import { Messages } from "../../../components/whatsApp/chat/chat/Messages/Messages";
import { HeaderChat } from "../../../components/whatsApp/chat/chat/HeaderChat";
import { MenuContacts } from "../../../components/whatsApp/menu/MenuContacts";
import { ChatInputBar } from '../../../components/whatsApp/chat/ChatInputBar';
import { ChatEmpty } from "../../../components/whatsApp/chat/chat/ChatEmpty";
import LoadingOverlay from '../../../components/others/LoadingOverlay';
import { getContacts, sendRequest, buildContent, createWebhookPayload } from "../../../services/whatsApp.service";
import { getInitialMessages, getTemplates, resetPendingMessages, getUsers } from "../../../services/whatsApp.service";
import { getCompanies } from '../../../services/client.service';
import { transformMessages } from '../../../utils/whatsApp/transformMessages';
 
import '../../../styles/components/_whatsApp.scss';
 
export default function WhatsAppLayout() {
    const [{ userSession }] = useDataAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const theme = useTheme();
   
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [openDialogCompany, setOpenDialogCompany] = useState(false);
    const [chatSelected, setChatSelected] = useState(null);
    const [companies, setCompanies] = useState([]);
    const [idCompany, setIdCompany] = useState('');
    const [loading, setLoading] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [messages, setMessages] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [users, setUsers] = useState([]);
    const chatContainerRef = useRef(null);
    const [view, setView] = useState('contacts');
    const { access, loadingAccess  } = useAccess("MENU-WHATSAPP", "", setLoading);
    useWebhookEvents(access?.viewAll, chatSelected, setMessages, setContacts);
 
    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    }, [messages]);
 
    useEffect(() => {
        if (loadingAccess) return;
        if (location.state && location.state.config) {
            const fetchAll = async () => {
                setLoading(true);
                try {
                    await fetchContacts(access?.viewAll);
                    await fetchDataCompany();
                } catch (e) {
                    console.error("Error fetching data", e);
                } finally {
                    setLoading(false);
                }
            };
            fetchAll();
        }
    }, [location.state, loadingAccess, access?.viewAll]);
 
    useEffect(() => {
        const fetchData = async () => {
            if (!idCompany) return;
            try {
                const response = await getTemplates(userSession.token, idCompany);
                if(access.changeResponsability){
                    const responseUsers = await getUsers(idCompany, userSession.token);
                    if(responseUsers?.data?.length){
                        setUsers(responseUsers.data)
                    }
                }
                if (response?.data?.length) {
                    setTemplates(response.data);
                }
            } catch (error) {
                console.error('Error fetching templates:', error);
            }
        };
        fetchData();
    }, [idCompany, userSession.token]);
 
    const fetchContacts = async (allAccess = false) => {
        try {
            const responseContacts = await getContacts(
                allAccess ? 'NOT_BY_USER' : "BY_USER",
                userSession.token
            );
            if (responseContacts?.data) {
                setContacts(responseContacts.data);
            }
        } catch (error) {
            console.error('Error fetching contacts:', error);
        }
    };
 
    const fetchDataCompany = async () => {
        try {
            const response = await getCompanies('77', userSession.token);
            const companiesData = response.data || [];
            setCompanies(companiesData);
            if (companiesData.length === 1) {
                const dto = companiesData[0];
                setIdCompany(dto.id_company);
                setOpenDialogCompany(false);
            } else {
                setOpenDialogCompany(true);
            }
        } catch (e) {
            console.error(e.message);
        }
    };
 
    const handleChatSelect = async (id_marketing_whatsapp) => {
        setChatSelected(contacts.find(user => user.id_marketing_whatsapp === id_marketing_whatsapp));
        setView('chat');
        const responseReset = await resetPendingMessages(userSession.token, id_marketing_whatsapp);
        if (responseReset && responseReset.data) {
            setContacts(prevUsers =>
                prevUsers.map(user => user.id_marketing_whatsapp === id_marketing_whatsapp ? { ...user, message_pending: 0 } : user)
            );
        }
        const responseMessages = await getInitialMessages(userSession.token, id_marketing_whatsapp);
        if (responseMessages && responseMessages.data) {
            transformMessages(responseMessages.data, setMessages);
        } else {
            setMessages([]);
        }
    };
 
    const handleSend = async ({ type, value, name = null, mimeType = null, animated = null }) => {
        if (!chatSelected) return;
        const content = buildContent({ type, value, name, mimeType, animated, chatSelected, idCompany });
        let payload = {};
        if (type !== 'template') {
            payload = createWebhookPayload({
                wa_id: chatSelected.cellphone,
                type,
                content,
                value,
                chatSelected,
                idCompany
            });
        } else {
            payload = content;
        }
        // console.log("message simulated:", JSON.stringify(payload, null, 2));
        sendRequest(userSession.token, payload);
        await delay(3000);
        const responseMessages = await getInitialMessages(
            userSession.token,
            chatSelected.id_marketing_whatsapp
        );
 
        if (responseMessages && responseMessages.data) {
            transformMessages(responseMessages.data, setMessages);
        }
    };
 
    const Cancel = () => {
        setOpenDialogCompany(false);
        navigate(-1)
    };
 
    const handleConfirmAction = useCallback(async (dto) => {
        setIdCompany(dto.id_company)
        setOpenDialogCompany(false);
    }, []);
 
    const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
 
    return (
        <Grid container sx={{ height: '100vh', backgroundColor: '#111b21', color: 'white' }}>
            <Grid size={{xs:12, md:2, lg:3 }}
                sx={{
                    display: (isMobile && view === 'chat') ? 'none' : 'block',
                    backgroundColor: '#202c33',
                    borderRight: '1px solid #2a3942',
                    height: '100vh',
                    transition: 'all 0.3s ease',
                }}
            >
                <MenuContacts
                    idCompany={idCompany}
                    contacts={contacts}
                    users={users}
                    access={access}
                    handleChatSelect={handleChatSelect}
                    fetchContacts={fetchContacts}
                />
            </Grid>
 
            {/* Chat window */}
            <Grid size={{xs:12, md:8, lg:9 }}
                sx={{
                    display: (isMobile && view === 'contacts') ? 'none' : 'flex',
                    flexDirection: 'column',
                    backgroundColor: '#111b21',
                    height: '100vh',
                    transition: 'all 0.3s ease',
                }}
            >
                {chatSelected ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                        <HeaderChat setContacts={setContacts} setChatSelected={(val) => { setChatSelected(val); setView('contacts'); }} chatSelected={chatSelected} />
                        <Messages messages={messages} />
                        <ChatInputBar handleSend={handleSend} chatSelected={chatSelected} templates={templates} messages={messages} />
                    </Box>
                ) : (
                    <ChatEmpty />
                )}
            </Grid>
            <DialogSelectCompany
                openDialogConfirm={openDialogCompany}
                handleCancelConfirm={Cancel}
                handleConfirmAction={handleConfirmAction}
                data={companies}
            />
            <LoadingOverlay isOpen={loading} />
        </Grid>
    );
}